import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import "./CreateOrganization.css";
import logoFM from "../../assets/logo_trucktronix.png";
import {
  serviceGetOrganizationDetails,
  serviceRegisterAdmin,
} from "../../api/serviceFleetMaster";

const CreateAdmin = () => {
  const navigate = useNavigate();

  const [orgCode, setOrgCode] = useState("");
  const [orgRole, setOrgRole] = useState("ADMIN");
  const [adminName, setAdminName] = useState("");
  const [adminPhone, setAdminPhone] = useState("");
  const [adminImageUrl, setAdminImageUrl] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [username, setUsername] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  const [loading, setLoading] = useState(false);

  const canSubmit = useMemo(() => {
    if (loading) return false;
    if (orgCode.trim().length !== 4) return false;
    if (!orgRole.trim()) return false;
    if (!adminName.trim()) return false;
    if (!adminEmail.trim()) return false;
    if (!username.trim()) return false;
    if (!adminPassword.trim()) return false;

    return true;
  }, [
    orgCode,
    orgRole,
    adminName,
    adminEmail,
    username,
    adminPassword,
    loading,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) return;

    setLoading(true);

    try {
      const payload = {
        code: orgCode.trim().toUpperCase(),
        role: orgRole,

        name: adminName.trim(),
        phone: adminPhone.trim(),
        imageUrl: adminImageUrl.trim(),

        email: adminEmail.trim(),
        username: username.trim(),
        pwd: adminPassword.trim(),
      };

      await serviceRegisterAdmin(payload);

      toast.success("Admin created successfully!");
      setTimeout(() => navigate("/"), 700);
    } catch (error) {
      // toast.error(`An unexpected error occurred: ${error.message}`);
      // if (response.status === 400) {
      //   toast.error("Invalid organization data or code already exists.");
      // } else {
      //   const msg = await response.text().catch(() => "");
      //   toast.error(`Failed to create organization.${msg ? ` ${msg}` : ""}`);
      // }

      if (error?.status === 400) {
        toast.error(error?.message || "Invalid admin details.");
      } else if (error?.status === 409) {
        toast.error(error?.message || "Invalid admin");
      } else {
        toast.error(
          error?.message ||
            "An unexpected error occurred while creating the admin.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="org-page">
      <header className="org-header">
        <button
          type="button"
          className="org-btn-back"
          onClick={() => navigate("/")}
          disabled={loading}
          title="Back"
        >
          <i className="bi bi-chevron-left" aria-hidden="true" />
          Back
        </button>

        <div className="org-header-right">
          <div className="org-header-title">
            <div className="org-header-title-main">Fleet Administration</div>
            <div className="org-header-title-sub">Setup New Admin</div>
          </div>
          <img src={logoFM} alt="Fleet Master" className="org-logo" />
        </div>
      </header>

      <main className="org-main">
        <div className="org-card">
          <div className="org-card-topbar">
            <div className="org-chip">
              <i className="bi bi-person-plus" aria-hidden="true" />
              Admin setup
            </div>

            {loading ? (
              <div className="org-status">
                <span className="org-spinner" aria-hidden="true" />
                Creating…
              </div>
            ) : (
              <div className="org-status is-idle">
                <i className="bi bi-check2" aria-hidden="true" />
                Ready
              </div>
            )}
          </div>

          <form className="org-form" onSubmit={handleSubmit} id="createAdmin">
            <div className="org-form-head">
              <div className="org-form-title">Register an Admin</div>

              <div className="org-form-sub">
                Create administrator credentials for this organization.
              </div>
            </div>

            <div className="org-grid">
              <div className="org-field">
                <label className="org-label" htmlFor="orgCode">
                  Organization Code
                </label>

                <input
                  id="orgCode"
                  type="text"
                  className="org-input"
                  placeholder="e.g. ABCD"
                  value={orgCode}
                  onChange={(e) =>
                    setOrgCode(
                      e.target.value
                        .replace(/[^a-zA-Z]/g, "")
                        .slice(0, 4)
                        .toUpperCase(),
                    )
                  }
                  maxLength={4}
                  disabled={loading}
                  required
                />

                <div className="org-hint">
                  Enter the four-letter organization code.
                </div>
              </div>

              <div className="org-field">
                <label className="org-label" htmlFor="orgRole">
                  Role
                </label>

                <select
                  id="orgRole"
                  className="org-input"
                  value={orgRole}
                  onChange={(e) => setOrgRole(e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="ADMIN">ADMIN</option>
                  <option value="MANAGEMENT">MANAGEMENT</option>
                </select>
              </div>

              <div className="org-field">
                <label className="org-label" htmlFor="adminName">
                  Admin Name
                </label>

                <input
                  id="adminName"
                  type="text"
                  className="org-input"
                  placeholder="e.g. Test Admin"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  disabled={loading}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="org-field">
                <label className="org-label" htmlFor="adminPhone">
                  Phone
                </label>

                <input
                  id="adminPhone"
                  type="tel"
                  className="org-input"
                  placeholder="e.g. 0412 345 678"
                  value={adminPhone}
                  onChange={(e) => setAdminPhone(e.target.value)}
                  disabled={loading}
                  autoComplete="tel"
                />
              </div>

              <hr className="org-divider" />

              <div className="org-field">
                <label className="org-label" htmlFor="adminEmail">
                  Admin Email
                </label>

                <input
                  id="adminEmail"
                  type="email"
                  className="org-input"
                  placeholder="admin@test.com.au"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="org-field">
                <label className="org-label" htmlFor="username">
                  Username
                </label>

                <input
                  id="username"
                  type="text"
                  className="org-input"
                  placeholder="user.name"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="org-field">
                <label className="org-label" htmlFor="adminPassword">
                  Admin Password
                </label>

                <input
                  id="adminPassword"
                  type="password"
                  className="org-input"
                  placeholder="••••••••"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>

            <div className="org-actions">
              <button
                className="org-btn org-btn-primary"
                type="submit"
                disabled={!canSubmit}
                title={
                  !canSubmit
                    ? "Complete all required fields to continue"
                    : undefined
                }
              >
                <i className="bi bi-person-plus" aria-hidden="true" />

                <span>{loading ? "Creating…" : "Create admin"}</span>
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CreateAdmin;
